import { lazy, Suspense, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Tooltip from "@mui/material/Tooltip";
import GrIconButton from "./GrIconButton";
import GrIcon from "./GrIcon";
import GrLoader from "./GrLoader";
import icons from "../icons";
import { gr, tokens } from "../tokens";

// Emoji button that opens a small emoji panel beside it (create post, comments): categories, search,
// frequently used, in the portal's light theme. The picker and its emoji list load only when the panel
// first opens, so they never weigh on the page load. `onPick(emoji)` gets the emoji character.
const Picker = lazy(() => import("@emoji-mart/react"));
const loadEmojiData = () => import("@emoji-mart/data").then((m) => m.default);

// emoji-mart reads its colours as "r, g, b" triplets, so the token hex values are converted here.
const toRgbTriplet = (hex) =>
  [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(", ");

const panelSx = {
  zIndex: (theme) => theme.zIndex.modal + 1,
  "& em-emoji-picker": {
    "--rgb-accent": toRgbTriplet(tokens.color.brandStrong),
    "--rgb-background": toRgbTriplet(tokens.color.surface),
    "--rgb-color": toRgbTriplet(tokens.color.ink),
    "--color-border": gr.color.line,
    "--color-border-over": gr.color.line,
    "--font-family": gr.font.family,
    "--font-size": gr.font.size.md,
    "--border-radius": gr.radius.card,
    "--shadow": gr.shadow.raised,
  },
};

export default function GrEmojiPicker({
  onPick,
  label = "Emoji",
  size = "md",
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ display: "inline-flex" }}>
        <Tooltip title={label} placement="top">
          <GrIconButton
            ref={anchorRef}
            variant="ghost"
            size={size}
            aria-label="Add emoji"
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={() => setOpen((value) => !value)}
          >
            <GrIcon icon={icons.smile} />
          </GrIconButton>
        </Tooltip>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          sx={panelSx}
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        >
          <Suspense fallback={<GrLoader size="sm" />}>
            <Picker
              data={loadEmojiData}
              theme="light"
              previewPosition="none"
              skinTonePosition="none"
              maxFrequentRows={1}
              perLine={8}
              onEmojiSelect={(emoji) => onPick(emoji.native)}
            />
          </Suspense>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

GrEmojiPicker.propTypes = {
  onPick: PropTypes.func.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md"]),
};
